import { Box, CircularProgress, Divider, styled, Typography } from "@mui/material";
import PropTypes from "prop-types";
import moment from "moment";

import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { currencySymbols } from "../../../constants/currency.constants";
import { ErrorMessage } from "../../../utils/commonUtils";
import { SUCCESS } from "../../../utils/constants";
import { SettingsActions } from "../../api/RequestUtil";
import AlertService from "../../common/Alert/alertService";

const SectionTitle = styled(Typography)({
  fontSize: "14px",
  fontWeight: 600,
  color: "#666",
  marginBottom: "8px",
});

const DetailRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "8px 0",
});

const Label = styled(Typography)({
  fontSize: "16px",
  color: "#333",
});

const Amount = styled(Typography)({
  fontSize: "16px",
  fontWeight: 500,
});

const AccountDetails = ({ selectedPlanId }) => {
  const { currency, dateFormat } = useSelector((state) => state.userSettings);
  const currentCurrencySymbol = currencySymbols.find((curr) => curr.value === currency)?.name;

  // Format date based on user's date format setting
  const formatDate = (dateString) => {
    if (!dateString) return "";
    // Convert database format (lowercase) to moment.js format (uppercase)
    const momentFormat = dateFormat ? dateFormat.toUpperCase() : "YYYY-MM-DD";
    return moment(dateString).format(momentFormat);
  };

  const transformApiResponse = (data) => {
    const { current_plan, new_plan, payment_amount, next_payment_date } = data;

    // Determine if this is a reactivation (current_plan with access_through instead of credit)
    const isReactivation = current_plan?.access_through;

    // Payment is due TODAY when:
    // 1. Upgrading (has current_plan with credit)
    // 2. New plan (no current_plan, but has payment_amount > 0)
    // 3. NOT reactivation (payment_amount = 0)
    const hasPaymentDueToday = payment_amount !== null && payment_amount !== undefined && payment_amount > 0;

    return {
      currentPlan: current_plan?.name
        ? {
            name: current_plan.name,
            startDate: formatDate(current_plan.start_date),
            endDate: formatDate(current_plan.end_date),
            remainingDays: current_plan.remaining_days,
            creditAmount: current_plan.credit_amount,
            accessThrough: current_plan.access_through, // For reactivation
            show: isReactivation
              ? true // Show for reactivation
              : current_plan.start_date &&
                current_plan.end_date &&
                current_plan.remaining_days &&
                current_plan.credit_amount !== null &&
                current_plan.credit_amount !== undefined,
          }
        : null,
      newPlan: new_plan?.name
        ? {
            name: new_plan.name,
            cost: new_plan.cost,
            startDate: formatDate(new_plan.start_date),
            show: true,
          }
        : null,
      payment: {
        amount: payment_amount,
        // Payment is due TODAY when there's an amount to pay (upgrades, reactivations, new plans)
        dueDate: hasPaymentDueToday ? formatDate(moment().format("YYYY-MM-DD")) : formatDate(next_payment_date),
        // Store the next billing cycle date separately
        nextBillingDate: formatDate(next_payment_date),
        show: payment_amount !== null && payment_amount !== undefined,
      },
    };
  };

  const [planDetails, setPlanDetails] = useState([]);
  const [loader, setLoader] = useState(false);
  const { error } = AlertService();

  const fetchPlanDetails = () => {
    setLoader(true);
    SettingsActions.getPlanDetails({
      new_plan_id: selectedPlanId,
    })
      .then(({ data }) => {
        if (data.status === SUCCESS) {
          const transformedData = transformApiResponse(data.data);
          setPlanDetails(transformedData);
        }
      })
      .catch((data) => error(ErrorMessage(data)))
      .finally(() => setLoader(false));
  };

  useEffect(() => {
    fetchPlanDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {loader ? (
        <Box display="flex" alignItems="center" justifyContent="center" height="65vh">
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <Box sx={{ padding: "8px 30px 24px 30px" }}>
          {/* Current Plan Section - for upgrades/downgrades OR reactivation */}
          {planDetails?.currentPlan?.show && (
            <>
              {planDetails.currentPlan.accessThrough ? (
                // Reactivation: Show access restoration info
                <>
                  <SectionTitle>Reactivating Your Subscription</SectionTitle>
                  <DetailRow>
                    <Label>{planDetails.currentPlan.name}</Label>
                  </DetailRow>
                  <DetailRow>
                    <Label sx={{ fontSize: "14px", color: "#666" }}>Paid Access Through</Label>
                    <Typography sx={{ fontSize: "14px", color: "#666" }}>
                      {formatDate(planDetails.currentPlan.accessThrough)}
                    </Typography>
                  </DetailRow>
                  <DetailRow>
                    <Label sx={{ fontSize: "14px", color: "#666" }}>Days Remaining</Label>
                    <Typography sx={{ fontSize: "14px", color: "#666" }}>
                      {planDetails.currentPlan.remainingDays} days
                    </Typography>
                  </DetailRow>
                  <Divider sx={{ margin: "16px 0" }} />
                </>
              ) : (
                // Upgrade/Downgrade: Show credit info
                <>
                  <SectionTitle>Current Plan Credit</SectionTitle>
                  <DetailRow>
                    <Label>{planDetails.currentPlan.name}</Label>
                  </DetailRow>
                  <DetailRow>
                    <Label sx={{ fontSize: "14px", color: "#666" }}>
                      {planDetails.currentPlan.startDate} - {planDetails.currentPlan.endDate}
                    </Label>
                    <Typography sx={{ fontSize: "14px", color: "#666" }}>
                      {planDetails.currentPlan.remainingDays} days remaining
                    </Typography>
                  </DetailRow>
                  <DetailRow sx={{ paddingTop: "4px" }}>
                    <Label>Credit Applied</Label>
                    <Amount sx={{ color: "#2e7d32" }}>
                      {currentCurrencySymbol} {parseFloat(planDetails.currentPlan.creditAmount).toFixed(2)}
                    </Amount>
                  </DetailRow>
                  <Divider sx={{ margin: "16px 0" }} />
                </>
              )}
            </>
          )}

          {/* New Plan Section */}
          {planDetails?.newPlan?.show && (
            <>
              <SectionTitle>New Plan</SectionTitle>
              <DetailRow>
                <Label>{planDetails.newPlan.name}</Label>
                <Amount>
                  {currentCurrencySymbol} {parseFloat(planDetails.newPlan.cost).toFixed(2)}
                </Amount>
              </DetailRow>
              {planDetails.newPlan.startDate && (
                <DetailRow>
                  <Label sx={{ fontSize: "14px", color: "#666" }}>Starts On</Label>
                  <Typography sx={{ fontSize: "14px", color: "#666" }}>{planDetails.newPlan.startDate}</Typography>
                </DetailRow>
              )}
              <Divider sx={{ margin: "16px 0" }} />
            </>
          )}

          {/* Payment Summary Section */}
          {planDetails?.payment?.show && (
            <>
              <SectionTitle>Payment Summary</SectionTitle>
              <DetailRow>
                <Label>Amount Due Today</Label>
                <Amount sx={{ color: planDetails.payment.amount < 0 ? "#2e7d32" : "#d32f2f", fontWeight: 600 }}>
                  {currentCurrencySymbol} {parseFloat(planDetails.payment.amount).toFixed(2)}
                </Amount>
              </DetailRow>
              <DetailRow>
                <Label sx={{ fontSize: "14px", color: "#666" }}>Due Date</Label>
                <Typography sx={{ fontSize: "14px", color: "#666" }}>{planDetails.payment.dueDate}</Typography>
              </DetailRow>
              <Divider sx={{ margin: "16px 0" }} />
            </>
          )}

          {/* Next Billing Section */}
          {planDetails?.newPlan?.show && planDetails?.payment?.nextBillingDate && (
            <>
              <SectionTitle>Next Billing Cycle</SectionTitle>
              <DetailRow>
                <Label>Recurring Monthly Charge</Label>
                <Amount>
                  {currentCurrencySymbol} {parseFloat(planDetails.newPlan.cost).toFixed(2)}
                </Amount>
              </DetailRow>
              <DetailRow>
                <Label sx={{ fontSize: "14px", color: "#666" }}>Starting</Label>
                <Typography sx={{ fontSize: "14px", color: "#666" }}>{planDetails.payment.nextBillingDate}</Typography>
              </DetailRow>
            </>
          )}
        </Box>
      )}
    </>
  );
};

AccountDetails.propTypes = {
  selectedPlanId: PropTypes.string,
};

export default AccountDetails;
